import { Box } from "@mui/material";
import { CountyPicker } from "components/CountyPicker";
import { CountryPicker } from "components/Facility/CountryPicker";
import { FormTextField } from "components/Forms/FormTextField";
import { StatePicker } from "components/StatePicker";
import { Controller, useFormContext } from "react-hook-form";

export type AddressType = "physical" | "mailing";

type AddressFieldNames = {
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
  country: string;
  latitude?: string;
  longitude?: string;
};

const fieldsByType: Record<AddressType, AddressFieldNames> = {
  physical: {
    streetAddress1: "streetAddress1",
    streetAddress2: "streetAddress2",
    city: "city",
    state: "state",
    zip: "zip",
    county: "county",
    country: "country",
    latitude: "latitude",
    longitude: "longitude",
  },
  mailing: {
    streetAddress1: "mailingStreetAddress1",
    streetAddress2: "mailingStreetAddress2",
    city: "mailingCity",
    state: "mailingState",
    zip: "mailingZip",
    country: "mailingCountry",
  },
};

/**
 * Shared address field grid. Used for Physical, Mailing, etc.
 * Must be rendered inside a parent with CSS grid `gridTemplateColumns: repeat(12, 1fr)`.
 */
export function AddressGrid({ type, disabled }: { type: AddressType; disabled?: boolean }) {
  const fields = fieldsByType[type];
  const { control, watch } = useFormContext();
  const state = (watch(fields.state) as string) ?? "";

  return (
    <>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField
          name={fields.streetAddress1}
          label="Street Address"
          control={control}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField
          name={fields.streetAddress2}
          label="Street Address 2"
          control={control}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormTextField name={fields.city} label="City" control={control} disabled={disabled} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <StatePicker
          name={fields.state}
          countryFieldName={fields.country}
          control={control}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormTextField name={fields.zip} label="Zip" control={control} disabled={disabled} />
      </Box>
      {fields.county && (
        <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
          <Controller
            name={fields.county}
            control={control}
            render={(renderProps) => (
              <CountyPicker
                {...(renderProps as Parameters<typeof CountyPicker>[0])}
                facilityState={state}
                disabled={disabled}
              />
            )}
          />
        </Box>
      )}
      <Box sx={{ gridColumn: { xs: "span 12", sm: fields.county ? "span 6" : "span 12" } }}>
        <CountryPicker name={fields.country} control={control} disabled={disabled} />
      </Box>
      {fields.latitude && fields.longitude && (
        <>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <FormTextField
              name={fields.latitude}
              label="Latitude"
              control={control}
              disabled={disabled}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <FormTextField
              name={fields.longitude}
              label="Longitude"
              control={control}
              disabled={disabled}
            />
          </Box>
        </>
      )}
    </>
  );
}
